import { useEffect, useRef, type RefObject } from 'react';

/**
 * Ce qui peut prendre le focus dans une fenêtre. Un élément masqué (`hidden`,
 * replié, `display: none`) n'en fait pas partie : la tabulation s'y perdrait.
 */
const FOCUSABLES =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, video[controls], audio[controls], [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

export function focusablesDe(conteneur: HTMLElement): HTMLElement[] {
  return [...conteneur.querySelectorAll<HTMLElement>(FOCUSABLES)].filter(
    (element) => visible(element) && !element.closest('[inert]'),
  );
}

/** `checkVisibility` manque aux navigateurs anciens : l'élément y passe pour visible. */
function visible(element: HTMLElement): boolean {
  return typeof element.checkVisibility === 'function' ? element.checkVisibility() : true;
}

/**
 * Fenêtres ouvertes, la plus récente en dernier. Deux fenêtres superposées
 * (une fiche, puis la visionneuse d'une pièce) écoutent toutes deux le
 * clavier : seule celle du dessus répond, un `Échap` ne ferme qu'elle.
 */
const pile: symbol[] = [];

/**
 * Fenêtre pilotable au clavier, comme l'exige le RGAA (critères 7.1 et 12.8,
 * modèle « dialog » de WAI-ARIA), partagée par la modale, le menu mobile et
 * les fenêtres plein écran (TF-341) :
 * - à l'ouverture, le focus entre dans la fenêtre — sur le conteneur, qui
 *   porte `tabIndex={-1}` et annonce son titre, ou sur la première commande ;
 *   un champ en `autoFocus` le garde ;
 * - `Échap` appelle `onEchap`, la tabulation tourne dans la fenêtre ;
 * - à la fermeture, le focus revient à ce qui l'avait avant l'ouverture.
 */
export function usePiegeFocus(
  conteneur: RefObject<HTMLElement>,
  actif: boolean,
  onEchap: () => void,
  { focusInitial = 'conteneur' }: { focusInitial?: 'conteneur' | 'premier' } = {},
): void {
  // `onEchap` est presque toujours recréée à chaque rendu du parent : lue par
  // une ref, elle ne rejoue pas l'effet — qui rendrait le focus au
  // déclencheur à chaque frappe dans un champ.
  const echap = useRef(onEchap);
  useEffect(() => {
    echap.current = onEchap;
  }, [onEchap]);

  // Le déclencheur se lit au rendu qui ouvre la fenêtre, avant qu'un champ en
  // `autoFocus` ne prenne le focus pendant le montage.
  const declencheur = useRef<Element | null>(null);
  const etaitActif = useRef(false);
  if (actif && !etaitActif.current && typeof document !== 'undefined') {
    declencheur.current = document.activeElement;
  }
  etaitActif.current = actif;

  useEffect(() => {
    const element = conteneur.current;
    if (!actif || !element) return;

    const jeton = Symbol('fenetre');
    pile.push(jeton);
    const auDessus = (): boolean => pile[pile.length - 1] === jeton;

    if (!element.contains(document.activeElement)) {
      const cible = focusInitial === 'premier' ? (focusablesDe(element)[0] ?? element) : element;
      cible.focus({ preventScroll: true });
    }

    const auClavier = (evenement: KeyboardEvent): void => {
      if (!auDessus()) return;
      if (evenement.key === 'Escape') {
        echap.current();
        return;
      }
      if (evenement.key !== 'Tab') return;

      const focusables = focusablesDe(element);
      if (focusables.length === 0) {
        evenement.preventDefault();
        element.focus();
        return;
      }
      const debut = focusables[0]!;
      const fin = focusables[focusables.length - 1]!;
      const courant = document.activeElement;
      // Focus sorti de la fenêtre (clic sur le fond) : on l'y ramène.
      if (!element.contains(courant)) {
        evenement.preventDefault();
        (evenement.shiftKey ? fin : debut).focus();
      } else if (evenement.shiftKey && (courant === debut || courant === element)) {
        evenement.preventDefault();
        fin.focus();
      } else if (!evenement.shiftKey && courant === fin) {
        evenement.preventDefault();
        debut.focus();
      }
    };

    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('keydown', auClavier);
      pile.splice(pile.indexOf(jeton), 1);
      const retour = declencheur.current as HTMLElement | null;
      if (retour?.isConnected) retour.focus?.({ preventScroll: true });
    };
  }, [actif, conteneur, focusInitial]);
}
